import { useCallback, useEffect, useState } from 'react';
import {
  addAllowedUser,
  listAllowedUsers,
  removeAllowedUser,
  setUserLimit,
  type AllowedUser,
} from '../lib/auth';

const PRESETS = [50, 100, 250, 500, 1000];

/** '' is the server default, 'none' is unlimited, anything else is a count. */
function limitValue(user: AllowedUser): string {
  if (user.fileLimit === undefined) return '';
  return user.fileLimit === 0 ? 'none' : String(user.fileLimit);
}

/** Owner-only panel for granting and revoking access. */
export function AdminUsers() {
  const [users, setUsers] = useState<AllowedUser[]>([]);
  const [owner, setOwner] = useState('');
  const [defaultLimit, setDefaultLimit] = useState(50);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const result = await listAllowedUsers();
      setUsers(result.users);
      setOwner(result.owner);
      setDefaultLimit(result.defaultLimit);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the access list');
    }
  }, []);

  useEffect(() => {
    if (open) void refresh();
  }, [open, refresh]);

  const handleAdd = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      const address = email.trim().toLowerCase();
      if (!address) return;

      setBusy(true);
      setError(null);
      setNotice(null);
      try {
        await addAllowedUser(address);
        setEmail('');
        setNotice(address + ' can now sign in.');
        await refresh();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not add that person');
      } finally {
        setBusy(false);
      }
    },
    [email, refresh],
  );

  const handleLimit = useCallback(
    async (address: string, value: string) => {
      // '' restores the server default, 'none' lifts the cap entirely.
      const limit = value === '' ? undefined : value === 'none' ? null : Number(value);

      setBusy(true);
      setError(null);
      setNotice(null);
      try {
        await setUserLimit(address, limit);
        setNotice(
          address +
            (limit === null
              ? ' can convert as many files at a time as they like.'
              : ' can convert ' + (limit ?? defaultLimit) + ' files at a time.'),
        );
        await refresh();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not change that limit');
      } finally {
        setBusy(false);
      }
    },
    [defaultLimit, refresh],
  );

  const handleRemove = useCallback(
    async (address: string) => {
      setBusy(true);
      setError(null);
      setNotice(null);
      try {
        await removeAllowedUser(address);
        // Their files stay on disk until the retention sweeper takes them, but
        // they lose access on their very next request.
        setNotice(address + ' can no longer sign in.');
        await refresh();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not remove that person');
      } finally {
        setBusy(false);
      }
    },
    [refresh],
  );

  return (
    <div className="card">
      <div className="results-head">
        <h2>Who can use this</h2>
        <button type="button" className="btn btn-quiet" onClick={() => setOpen((v) => !v)}>
          {open ? 'Hide' : 'Manage access'}
        </button>
      </div>

      {open ? (
        <>
          {error ? <div className="notice notice-error">{error}</div> : null}
          {notice ? <div className="notice">{notice}</div> : null}

          <form className="admin-add" onSubmit={handleAdd}>
            <input
              type="email"
              className="admin-input"
              placeholder="name@example.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              disabled={busy}
              aria-label="Email address to grant access to"
            />
            <button type="submit" className="btn btn-primary" disabled={busy || !email.trim()}>
              Grant access
            </button>
          </form>

          <ul className="admin-list">
            <li className="admin-row">
              <span className="admin-email">{owner || 'the owner'}</span>
              <span className="admin-limit-fixed">no limit</span>
              <span className="admin-tag">owner</span>
            </li>
            {users.map((user) => {
              const current = limitValue(user);
              // A limit set through the API need not be one of the presets, so
              // keep it in the list rather than silently showing another value.
              const options = PRESETS.includes(Number(current)) || current === '' || current === 'none'
                ? PRESETS
                : [Number(current), ...PRESETS].sort((a, b) => a - b);

              return (
                <li key={user.email} className="admin-row">
                  <span className="admin-email">{user.email}</span>
                  <select
                    className="admin-limit"
                    value={current}
                    disabled={busy}
                    aria-label={'Files per batch for ' + user.email}
                    onChange={(event) => handleLimit(user.email, event.target.value)}
                  >
                    <option value="">Default ({defaultLimit} files)</option>
                    {options.map((n) => (
                      <option key={n} value={n}>
                        {n.toLocaleString()} files
                      </option>
                    ))}
                    <option value="none">Unlimited</option>
                  </select>
                  <button
                    type="button"
                    className="btn btn-quiet"
                    disabled={busy}
                    onClick={() => handleRemove(user.email)}
                  >
                    Remove
                  </button>
                </li>
              );
            })}
          </ul>

          {users.length === 0 ? (
            <p className="signin-lede">Nobody else has access yet. Add an email address above.</p>
          ) : (
            <p className="signin-lede">
              The limit is how many files somebody can convert in one go. It does not change
              retention: every file is deleted after 24 hours, for every account.
            </p>
          )}
        </>
      ) : null}
    </div>
  );
}
